/* eslint-disable */
import auth0Js from 'auth0-js';
import { jwtDecode } from 'jwt-decode';
import { TOGGLE_GLOBAL_TOAST } from '../store/actions/action-types';
import store from '../store/configure-store';

const handleToast = (label, type = 'error') =>
  store.dispatch({
    type: TOGGLE_GLOBAL_TOAST,
    payload: {
      visibility: true,
      label,
      type,
    },
  });

const environmentMap = {
  development: 'test',
  test: 'test',
  stage: 'stage',
  production: 'prod',
};
const stage = environmentMap[process.env.REACT_APP_ENVIRONMENT] || 'test';

const auth0 = new auth0Js.WebAuth({
  domain: process.env.REACT_APP_AUTH0_DOMAIN,
  clientID: process.env.REACT_APP_AUTH0_CLIENT_ID,
  redirectUri: `${window.location.origin}/signin/callback`,
  audience: process.env.REACT_APP_AUTH0_AUDIENCE,
  responseType: 'token id_token',
  scope: 'openid profile email',
});

class Auth {
  constructor() {}

  removeUserData = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('user_id');
  };

  login = async ({ email, password }) => {
    this.removeUserData();
    let resolve;
    let reject;
    const loginPromise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    await auth0.login(
      {
        email,
        password,
        scope: 'openid',
        connection: 'Username-Password-Authentication',
        stage,
      },
      (e) => reject(e),
    );
    return loginPromise;
  };

  socialLogin = async () => {
    this.removeUserData();
    let resolve;
    let reject;
    const socialLoginPromise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    await auth0.authorize({ connection: 'google-oauth2', stage }, (e) => {
      handleToast(e.description);
      reject(e.description);
    });
    return socialLoginPromise;
  };

  authenticationCallback = async () => {
    let resolve;
    let reject;
    const authenticatePromise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    auth0.parseHash({}, (err, authResult) => {
      if (err) {
        console.log(`Error ${err} Occurred`);
        console.log(JSON.stringify(err));
        console.trace(err);
        handleToast(err.error);
        return reject(err.error);
      }
      this.setSession(authResult);
      // handleToast('success!', 'success');
      resolve(jwtDecode(authResult.accessToken).quickclaim);
    });
    return authenticatePromise;
  };

  getOrgId = () => {
    const accessToken = localStorage.getItem('access_token');
    const orgId = accessToken ? jwtDecode(accessToken)?.quickclaim.orgId : null;
    if (!orgId) {
      return undefined;
    }
    return orgId;
  };

  getAccessToken = () => {
    const accessToken = localStorage.getItem('access_token');
    if (!accessToken) {
      return undefined;
    }
    return accessToken;
  };

  loginMultiOrgUser = async (orgId) => {
    let resolve;
    let reject;
    const continuePromise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    this.removeUserData();
    await auth0.authorize({ stage, orgId }, (e, data) => {
      if (e) {
        reject(e);
      } else {
        this.setSession(data);
        resolve(data);
      }
    });

    return continuePromise;
  };

  signUpUser = async ({ email, password, given_name, family_name }) => {
    let resolve;
    let reject;
    const signUpPromise = new Promise((res, rej) => {
      resolve = res;
      reject = rej;
    });
    await auth0.signupAndAuthorize(
      {
        connection: 'Username-Password-Authentication',
        email,
        password,
        family_name,
        given_name,
        stage,
      },
      (e, data) => {
        if (e) {
          reject(e);
        } else {
          // console.log('DATA', data);
          this.setSession(data);
          resolve(data);
        }
      },
    );
    return signUpPromise;
  };

  logout = () => {
    this.removeUserData();
    localStorage.removeItem('return_to');
    auth0.logout({
      returnTo: `${window.location.origin}/signin`, // toDo
    });
  };

  setSession = (authResult) => {
    const { accessToken, idToken, expiresIn } = authResult;
    // set the time the access token will expire
    const expiresAt = JSON.stringify(expiresIn * 1000 + new Date().getTime());

    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('id_token', idToken);
    localStorage.setItem('expires_at', expiresAt);
    localStorage.setItem('user_id', jwtDecode(idToken).sub);
  };

  isAuthenticated = () => {
    // Check whether the current time is past the
    // access token's expiry time
    const expiresAt = JSON.parse(localStorage.getItem('expires_at'));
    return this.getAccessToken() !== undefined && new Date().getTime() < expiresAt;
  };

  getUserId = () => {
    if (localStorage.getItem('user_id')) {
      return localStorage.getItem('user_id');
    }
  };

  changePassword = (email) => {
    auth0.changePassword(
      {
        connection: 'Username-Password-Authentication',
        email,
      },
      (err, resp) => {
        if (err) {
          handleToast(err.message);
        } else {
          handleToast(resp, 'success');
        }
      },
    );
  };

  shouldRenewToken = () => {
    const expiresAt = JSON.parse(localStorage.getItem('expires_at'));
    if (!expiresAt) {
      return false;
    }

    // Check if token is older than 30 minutes (1800000 milliseconds)
    const thirtyMinutes = 30 * 60 * 1000;
    const tokenAge = expiresAt - new Date().getTime();
    return tokenAge < thirtyMinutes && tokenAge > 0 && this.getAccessToken();
  };

  refreshTokenPromise = null;

  renewToken = async () => {
    if (!this.refreshTokenPromise) {
      const orgId = this.getOrgId();
      this.refreshTokenPromise = new Promise((resolve, reject) => {
        auth0.checkSession({ stage, orgId }, (err, authResult) => {
          if (err) {
            console.error('Token renewal error:', err);
            this.refreshTokenPromise = null;
            this.logout();
            return reject(err);
          }
          console.log('Token renewal');
          this.setSession(authResult);
          resolve(authResult);
          this.refreshTokenPromise = null;
        });
      });
    }
    return this.refreshTokenPromise;
  };
}

let instance;
export default () => {
  if (instance) {
    return instance;
  }
  instance = new Auth();
  return instance;
};
